import React, { useState, useEffect } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { BellIcon, CheckCircleIcon, ExclamationCircleIcon, InformationCircleIcon, BellAlertIcon, MegaphoneIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { NotificationService, Notification } from '../services/NotificationService';

const notificationService = new NotificationService();

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const unreadCount = notifications.filter(n => n.status !== 'read').length;

  // Încarcă notificările
  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await notificationService.getNotifications();
      setNotifications(data);
    } catch (err: any) {
      console.error('Error loading notifications:', err);
      setError('Eroare la încărcarea notificărilor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();

    // Polling la fiecare 30 de secunde pentru notificări noi
    const intervalId = setInterval(loadNotifications, 30000);

    return () => clearInterval(intervalId);
  }, []);

  const markAsRead = async (id: number) => {
    try {
      await notificationService.markNotificationAsRead(id);

      // Actualizare optimistă în UI
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, status: 'read' as const, read_at: new Date().toISOString() } : n)
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllNotificationsAsRead();

      // Actualizare optimistă în UI
      const now = new Date().toISOString();
      setNotifications(prev =>
        prev.map(n => ({ ...n, status: 'read' as const, read_at: now }))
      );
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const deleteNotification = async (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await notificationService.deleteNotification(id);

      // Eliminare din UI
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    const iconClasses = "h-5 w-5 flex-shrink-0";

    switch (type) {
      case 'reminder':
        return <BellAlertIcon className={`${iconClasses} text-blue-500`} />;
      case 'system':
        return <InformationCircleIcon className={`${iconClasses} text-gray-500`} />;
      case 'announcement':
        return <MegaphoneIcon className={`${iconClasses} text-purple-500`} />;
      case 'alert':
        return <ExclamationCircleIcon className={`${iconClasses} text-red-500`} />;
      default:
        return <InformationCircleIcon className={`${iconClasses} text-blue-500`} />;
    }
  };

  const getNotificationBackground = (type: Notification['type'], isUnread: boolean) => {
    if (!isUnread) return '';

    switch (type) {
      case 'reminder':
        return 'bg-blue-50 dark:bg-blue-900/20';
      case 'system':
        return 'bg-gray-50 dark:bg-dm-hover';
      case 'announcement':
        return 'bg-purple-50 dark:bg-purple-900/20';
      case 'alert':
        return 'bg-red-50 dark:bg-red-900/20';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20';
    }
  };

  const getTimeAgo = (date: string) => {
    return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ro });
  };

  const handleMarkAsRead = async (id: number, event: React.MouseEvent) => {
    event.stopPropagation();
    await markAsRead(id);
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Navighează către entitatea asociată dacă există
    if (notification.related_entity_type && notification.related_entity_id) {
      const entityRoutes: Record<string, string> = {
        'reminders': `/calendar`,
        'orders': `/orders/${notification.related_entity_id}`,
        'users': `/users/${notification.related_entity_id}`,
        'clients': `/crm/clients/${notification.related_entity_id}`,
        'activities': `/crm/activities/${notification.related_entity_id}`,
        'containers': `/containers/${notification.related_entity_id}`,
        'bookings': `/bookings/${notification.related_entity_id}`,
      };

      const route = entityRoutes[notification.related_entity_type];
      if (route) {
        navigate(route);
      }
    }
  };

  const handleItemClick = (e: React.MouseEvent, notification: Notification) => {
    // Verifică dacă utilizatorul a selectat text
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return; // Nu face nimic dacă este selectat text
    }

    handleNotificationClick(notification);
    // Not calling close() so it stays open as requested
  };

  const markdownComponents = {
    table: ({node, ...props}: any) => <div className="overflow-x-auto my-2 rounded-lg bg-gray-50 dark:bg-dm-hover border border-gray-200 dark:border-dm-border w-[470px] -ml-[33px]" onClick={(e) => e.stopPropagation()}><table className="min-w-full divide-y divide-gray-200 dark:divide-dm-border text-sm" {...props} /></div>,
    thead: ({node, ...props}: any) => <thead className="bg-gray-50 dark:bg-dm-surface" {...props} />,
    th: ({node, ...props}: any) => <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-dm-text-muted uppercase tracking-wider border-b border-gray-200 dark:border-dm-border" {...props} />,
    td: ({node, ...props}: any) => <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600 dark:text-dm-text-secondary border-b border-gray-200 dark:border-dm-border" {...props} />,
    ul: ({node, ...props}: any) => <ul className="list-disc pl-5 my-1" {...props} />,
    ol: ({node, ...props}: any) => <ol className="list-decimal pl-5 my-1" {...props} />,
    a: ({node, ...props}: any) => <a className="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} {...props} />,
    p: ({node, ...props}: any) => <p className="mb-1 last:mb-0" {...props} />,
  };

  return (
    <Popover className="relative inline-block text-left">
      <div>
        <PopoverButton className="relative p-1 text-gray-700 dark:text-dm-text-muted hover:text-gray-900 dark:hover:text-dm-text focus:outline-none rounded-full hover:bg-gray-100 dark:hover:bg-dm-surface transition-colors">
          <BellIcon className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full animate-pulse">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </PopoverButton>
      </div>

      <PopoverPanel
        transition
        className="absolute right-0 z-10 mt-2 max-w-[500px] w-auto min-w-[24rem] origin-top-right rounded-lg bg-white dark:bg-dm-surface shadow-xl ring-1 ring-black ring-opacity-5 dark:ring-dm-border transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-gray-200 dark:border-dm-border flex items-center justify-between bg-gray-50 dark:bg-dm-bg rounded-t-lg">
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-dm-text">Notificări</p>
            {unreadCount > 0 && (
              <p className="text-xs text-gray-500 dark:text-dm-text-muted">{unreadCount} necitit{unreadCount === 1 ? 'ă' : 'e'}</p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              Marchează toate
            </button>
          )}
        </div>

        {/* Content */}
        <div className="max-h-[32rem] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-dm-text-muted">Se încarcă...</p>
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center">
              <ExclamationCircleIcon className="h-12 w-12 mx-auto text-red-300 mb-2" />
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={loadNotifications}
                className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                Încearcă din nou
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <BellIcon className="h-12 w-12 mx-auto text-gray-300 dark:text-dm-text-subtle mb-2" />
              <p className="text-sm text-gray-500 dark:text-dm-text-muted">Nu există notificări</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-dm-border">
              {notifications.map((notification) => {
                const isUnread = notification.status !== 'read';
                return (
                  <div
                    key={notification.id}
                    onClick={(e) => handleItemClick(e, notification)}
                    className={`px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-dm-hover transition-colors relative group ${
                      getNotificationBackground(notification.type, isUnread)
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 ${!isUnread ? 'opacity-50' : ''}`}>
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className={`flex-1 min-w-0 ${isUnread ? 'pr-14' : 'pr-10'}`}>
                        <div className="flex items-start justify-between mb-1">
                          <p className={`text-sm ${isUnread ? 'font-semibold text-gray-900 dark:text-dm-text' : 'font-medium text-gray-600 dark:text-dm-text-secondary'}`}>
                            {notification.title || 'Notificare'}
                          </p>
                        </div>
                        <div className={`text-sm mb-1 ${isUnread ? 'text-gray-600 dark:text-dm-text-secondary' : 'text-gray-500 dark:text-dm-text-muted'}`}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {notification.message}
                          </ReactMarkdown>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className={`text-xs ${isUnread ? 'text-gray-500 dark:text-dm-text-muted' : 'text-gray-400 dark:text-dm-text-subtle'}`}>
                            {getTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="absolute top-3 right-3 flex items-center gap-1">
                        {isUnread && (
                          <button
                            onClick={(e) => handleMarkAsRead(notification.id, e)}
                            className="p-1.5 rounded-full hover:bg-green-100 dark:hover:bg-green-900/30 transition-all group/check"
                            title="Marchează ca citită"
                          >
                            <CheckCircleIcon className="h-5 w-5 text-green-600 group-hover/check:text-green-700" />
                          </button>
                        )}
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          className="p-1.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
                          title="Șterge notificarea"
                        >
                          <TrashIcon className="h-4 w-4 text-red-600 hover:text-red-700" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-2 py-2 border-t border-gray-200 dark:border-dm-border bg-gray-50 dark:bg-dm-bg rounded-b-lg">
            <button
              onClick={loadNotifications}
              className="block w-full text-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium py-1 hover:bg-white dark:hover:bg-dm-hover rounded transition-colors"
            >
              Reîmprospătează notificările
            </button>
          </div>
        )}
      </PopoverPanel>
    </Popover>
  );
};

export default NotificationBell;
